import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Mail, ShieldCheck, Send, Clock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateGB } from "../lib/format";

const ROLES = [
  { value: "admin", label: "Admin", desc: "Full system access" },
  { value: "manager", label: "Project Manager", desc: "Quotes, signoff, users" },
  { value: "asset_manager", label: "Asset Manager", desc: "Portfolio pipeline owner" },
  { value: "epc", label: "EPC / Installer", desc: "Design + install execution" },
  { value: "contractor", label: "Contractor", desc: "Site/sub-contractor tasks" },
  { value: "investor", label: "Investor", desc: "Read-only portfolio access" },
  { value: "customer", label: "Customer", desc: "End-customer portal access" },
];

const ROLE_TONE = {
  admin: "ink",
  manager: "signal",
  asset_manager: "signal",
  epc: "ok",
  contractor: "warn",
  investor: "ink",
  customer: "ok",
};

export default function Users() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = user?.role === "admin" || user?.role === "manager";

  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null); // {mode:"create"|"edit", data}

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return users;
    return users.filter(u => u.role === filter);
  }, [users, filter]);

  const onDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.name}" (${u.email})? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const onResendInvite = async (u) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${u.id}/resend-invite`);
      if (data.email_status === "sent") {
        toast.success(`Invitation re-sent to ${u.email}`);
      } else {
        toast.error(`User updated, but email failed: ${data.email_status || "unknown"}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const counts = useMemo(() => {
    const acc = { all: users.length };
    for (const r of ROLES) acc[r.value] = users.filter(u => u.role === r.value).length;
    return acc;
  }, [users]);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="users-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label">Execution</div>
          <h1 className="font-display text-4xl font-bold mt-1">Users</h1>
          <p className="muted text-sm mt-2">
            Create and manage EPCs, contractors, investors, customers and managers across all projects.
          </p>
        </div>
        {canManage && (
          <button
            className="btn-accent"
            onClick={() => setModal({ mode: "create", data: { email: "", name: "", role: "epc", org: "", password: "" } })}
            data-testid="user-create-btn"
          >
            <Plus size={14} /> New user
          </button>
        )}
      </div>

      {/* Role filter chips */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="user-role-filters">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="user-filter-all">
          All <span className="ml-1 font-mono opacity-60">{counts.all}</span>
        </FilterChip>
        {ROLES.map(r => (
          <FilterChip
            key={r.value}
            active={filter === r.value}
            onClick={() => setFilter(r.value)}
            testid={`user-filter-${r.value}`}
          >
            {r.label} <span className="ml-1 font-mono opacity-60">{counts[r.value] || 0}</span>
          </FilterChip>
        ))}
      </div>

      <div className="swiss-card-flat overflow-hidden">
        <table className="data-table" data-testid="users-table">
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Organisation</th><th>Created</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-medium">
                  {u.name}
                  {u.id === user?.id && <span className="ml-2 label !text-[9px] !text-[var(--accent-hover)]">(you)</span>}
                </td>
                <td className="font-mono text-xs"><Mail size={11} className="inline mr-1 muted" />{u.email}</td>
                <td><span className={`tag ${ROLE_TONE[u.role] || ""}`}>{u.role}</span></td>
                <td>
                  {u.invite_pending ? (
                    <span className="tag warn inline-flex items-center gap-1" data-testid={`user-status-pending-${u.id}`}>
                      <Clock size={9} /> invited
                    </span>
                  ) : (
                    <span className="tag ok" data-testid={`user-status-active-${u.id}`}>active</span>
                  )}
                </td>
                <td className="muted">{u.org || "—"}</td>
                <td className="font-mono text-xs muted">{fmtDateGB(u.created_at)}</td>
                <td className="num">
                  {canManage && u.invite_pending && (
                    <button
                      onClick={() => onResendInvite(u)}
                      disabled={busy}
                      className="mr-2"
                      data-testid={`user-resend-${u.id}`}
                      title="Resend invitation email"
                    >
                      <Send size={13} className="muted" />
                    </button>
                  )}
                  {canManage && (
                    <button
                      onClick={() => setModal({ mode: "edit", data: { ...u, password: "" } })}
                      className="mr-2"
                      data-testid={`user-edit-${u.id}`}
                      title="Edit user"
                    >
                      <Pencil size={13} className="muted" />
                    </button>
                  )}
                  {isAdmin && u.id !== user?.id && (
                    <button
                      onClick={() => onDelete(u)}
                      disabled={busy}
                      data-testid={`user-del-${u.id}`}
                      title="Delete user"
                    >
                      <Trash2 size={13} className="muted" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={7} className="muted text-center py-8" data-testid="users-empty">No users in this view.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <UserModal
          mode={modal.mode}
          initial={modal.data}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        active
          ? "bg-[var(--accent)] text-white border-[var(--accent)]"
          : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--accent)]"
      }`}
    >
      {children}
    </button>
  );
}

function UserModal({ mode, initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial, send_invite: mode === "create" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "create") {
        if (!form.send_invite) {
          if (!form.password || form.password.length < 6) {
            toast.error("Password must be at least 6 characters");
            setBusy(false); return;
          }
        }
        const payload = {
          email: form.email,
          name: form.name,
          role: form.role,
          org: form.org || null,
          send_invite: !!form.send_invite,
        };
        if (!form.send_invite) payload.password = form.password;
        const { data } = await api.post("/users", payload);
        if (form.send_invite) {
          if (data.email_status === "sent") {
            toast.success(`Invitation email sent to ${form.email}`);
          } else {
            toast.warning(`User created — but email failed: ${data.email_status || "unknown"}`);
          }
        } else {
          toast.success("User created");
        }
      } else {
        const payload = {
          name: form.name,
          role: form.role,
          org: form.org || null,
        };
        if (form.password && form.password.length >= 6) payload.password = form.password;
        await api.patch(`/users/${form.id}`, payload);
        toast.success("User updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="user-modal">
      <form
        onSubmit={submit}
        className="modal-card p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="user-modal-form"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label">{mode === "create" ? "Create" : "Edit"}</div>
            <div className="font-display text-xl font-semibold mt-1">
              {mode === "create" ? "New user" : "Edit user"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="muted" data-testid="user-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <span className="label block mb-1">Full name *</span>
            <input
              required
              className="input-swiss"
              value={form.name || ""}
              onChange={upd("name")}
              data-testid="user-name-input"
            />
          </label>
          <label className="block">
            <span className="label block mb-1">Email *</span>
            <input
              required
              type="email"
              className="input-swiss"
              value={form.email || ""}
              onChange={upd("email")}
              disabled={mode === "edit"}
              data-testid="user-email-input"
            />
          </label>
          <label className="block">
            <span className="label block mb-1">Organisation</span>
            <input
              className="input-swiss"
              value={form.org || ""}
              onChange={upd("org")}
              placeholder="optional"
              data-testid="user-org-input"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="label block mb-1">Role *</span>
            <select className="input-box" value={form.role} onChange={upd("role")} data-testid="user-role-input">
              {ROLES.map(r => (
                <option key={r.value} value={r.value}>{`${r.label} — ${r.desc}`}</option>
              ))}
            </select>
          </label>

          {mode === "create" && (
            <div className="md:col-span-2" data-testid="user-mode-picker">
              <div className="label mb-2">Activation</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, send_invite: true }))}
                  className={`p-3 rounded-md border text-left transition-colors ${
                    form.send_invite
                      ? "border-[var(--accent)] bg-[var(--accent-soft)]"
                      : "border-[var(--line)] hover:border-[var(--accent)]"
                  }`}
                  data-testid="user-mode-invite"
                >
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: form.send_invite ? "var(--accent-hover)" : "var(--ink-2)" }}>
                    <Mail size={12} /> Email invitation
                  </div>
                  <div className="text-[11px] muted mt-1 leading-relaxed">
                    Recipient picks their own password via a one-time link (expires in 7 days). Recommended.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, send_invite: false }))}
                  className={`p-3 rounded-md border text-left transition-colors ${
                    !form.send_invite
                      ? "border-[var(--accent)] bg-[var(--accent-soft)]"
                      : "border-[var(--line)] hover:border-[var(--accent)]"
                  }`}
                  data-testid="user-mode-password"
                >
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: !form.send_invite ? "var(--accent-hover)" : "var(--ink-2)" }}>
                    <ShieldCheck size={12} /> Set password now
                  </div>
                  <div className="text-[11px] muted mt-1 leading-relaxed">
                    You set the initial password and share it out-of-band (Slack, phone, in person).
                  </div>
                </button>
              </div>
            </div>
          )}

          {(mode === "edit" || !form.send_invite) && (
            <label className="block md:col-span-2">
              <span className="label block mb-1">
                {mode === "create" ? "Initial password * (min 6)" : "Reset password (optional, min 6)"}
              </span>
              <input
                type="password"
                className="input-swiss"
                value={form.password || ""}
                onChange={upd("password")}
                minLength={6}
                required={mode === "create" && !form.send_invite}
                data-testid="user-password-input"
                placeholder={mode === "edit" ? "leave blank to keep current" : ""}
              />
            </label>
          )}
        </div>

        <div className="mt-3 text-[11px] muted flex items-start gap-2">
          <ShieldCheck size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            {mode === "create" && form.send_invite
              ? "The recipient will receive a Flow-branded email with a secure activation link. Make sure RESEND_API_KEY is configured in Settings."
              : "New users receive their password from you out-of-band. They can change it via Settings after their first sign-in."}
          </span>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="user-modal-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-accent" data-testid="user-modal-save">
            {busy
              ? "Saving…"
              : mode === "create"
                ? (form.send_invite ? "Send invitation" : "Create user")
                : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
